<template>
	<Modal
		:modal-name="modalName"
		type="dialog-md"
		:theme="$arenaConfig.isLightTheme ? null : 'dark'"
		:show-close-on-frameless="true"
		class="follow-search-modal"
		@closing-modal="$emit('close')"
		@opened="onOpen"
	>
		<template #title>
			{{ $Helpers.ucfirst($I18n.trans('arena.fan_account.following.follow_favorites')) }}
		</template>
		<template #content>
			<div class="follow-search-modal__content">
				<div
					v-adapt-bg-color
					class="follow-search-modal__content-header"
				>
					<InputText
						v-model="search"
						type="search"
						:label="$I18n.trans('commons.type_search', { type: $I18n.trans_choice('commons.club', 2)})"
						:label-visually-hidden="true"
						:dark-theme="$arenaConfig.isLightTheme ? false : true"
						:placeholder="$I18n.trans('commons.type_search', { type: $I18n.trans_choice('commons.club', 2)})"
					/>
				</div>
				<div
					v-if="filteredFollowables.length > 0"
					class="follow-search-modal__followables"
				>
					<div
						v-for="followable in filteredFollowables"
						:key="`${followable.context.id}-${followable.contextable.id}`"
						class="follow-search-modal__followable"
					>
						<div class="follow-search-modal__followable-name">
							<img
								:src="$Helpers.getScaledImageUrl(followable.contextable.imageUrl, 48, { heightIsWidth: true })"
								:alt="followable.contextable.name"
								class="follow-search-modal__followable-image"
							>
							{{ followable.contextable.name }}
						</div>
						<FollowButton
							:dark-theme="$arenaConfig.isLightTheme ? false : true"
							:followable="followable"
							:is-following="followable.isFollowing"
							:is-following-in-progress="followable.isFollowingInProgress"
							:is-locked="followable.isLocked"
						/>
					</div>
				</div>
				<LoadingSpinner
					v-else-if="isLoading"
					class="follow-search-modal__loading-spinner"
				/>
				<div
					v-else
					class="follow-search-modal__empty-state"
				>
					{{ $I18n.trans('commons.no_results') }}
				</div>
			</div>
		</template>
	</Modal>
</template>

<script>
import Modal from '@shared/components/Modal';
import InputText from '@shared/components/Form/InputText';
import FollowButton from '@js/Pages/FanAccount/partials/FollowButton';
import { mapActions, mapGetters } from 'vuex';
import { actions } from '@shared/js/store/modules/following';
import vAdaptBgColor from '@shared/directives/v-adapt-bg-color';
import LoadingSpinner from '@shared/components/LoadingSpinner';

export default {
	name:       'ModalFollowSearch',
	components: {
		Modal,
		InputText,
		FollowButton,
		LoadingSpinner,
	},
	directives: {
		'adapt-bg-color': vAdaptBgColor,
	},
	props: {
		modalName: {
			type:     String,
			required: true,
		},
	},
	data() {
		return {
			search:    '',
			isLoading: false,
		};
	},
	computed: {
		...mapGetters({
			follows:             'following/follows',
			followables:         'following/followables',
			followInProgressFor: 'following/followInProgressFor',
		}),
		filteredFollowables() {
			const followables = this.followables.map((followable) => ({
				...followable,
				isFollowingInProgress: this.followInProgressFor.some((item) => item.context.id === followable.context.id && item.contextable.id === followable.contextable.id),
				isFollowing:           this.follows.some((item) => item.context.id === followable.context.id && item.contextable.id === followable.contextable.id),
				isLocked:              this.follows.some((item) => item.context.id === followable.context.id && item.contextable.id === followable.contextable.id && item.locked),
			}));

			if (!this.search) {
				return followables;
			}
			return followables.filter((followable) => followable.contextable.name.toLowerCase().includes(this.search.toLowerCase()));
		},
	},
	methods: {
		...mapActions({
			loadFollowables: `following/${actions.LOAD_FOLLOWABLES}`,
			loadFollows:     `following/${actions.LOAD_FOLLOWS}`,
		}),
		async onOpen() {
			this.isLoading = true;
			await this.loadFollowables();
			await this.loadFollows();
			this.isLoading = false;
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';

.follow-search-modal {
	::v-deep {
		.v2-modal__content {
			padding-top: 0;
		}
	}
}

// .follow-search-modal__content {}

.follow-search-modal__content-header {
	position: sticky;
	top: 0;
	padding-top: $sp5;
	padding-bottom: $sp3;
}

.follow-search-modal__title {
	display: none;

	@include size(md-up) {
		display: block;
		margin-bottom: $sp6 !important; // stylelint-disable-line
	}
}

.follow-search-modal__followables {
	display: flex;
	flex-direction: column;
}

.follow-search-modal__followable {
	display: flex;
	justify-content: space-between;
	align-items: center;
	border-bottom: 1px solid var(--color-grey-900);
	padding: $sp3 0;
}

.follow-search-modal__followable-name {
	@include font-size(fs-80);
}

.follow-search-modal__followable-image {
	width: 24px;
	margin-right: $sp2;
}

.follow-search-modal__empty-state {
	text-align: center;
	padding-top: $sp3;
	padding-bottom: $sp3;
}

.follow-search-modal__loading-spinner {
	margin-left: auto;
	margin-right: auto;
	display: flex;
	justify-self: center;
}
</style>
